import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { Heart, MoreHorizontal, Share2, PlayCircle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../misc/Avatar";
import { MediaType } from "../../../types/media";
import { PinterestPostProps } from "../types";
import { GoSmiley } from "react-icons/go";
import { PiSticker } from "react-icons/pi";
import { GrGallery } from "react-icons/gr";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { calculateFittedFontSize } from "../../../utils/autoFitFont";
import { withoutLeadGenerationBody } from "../util/leadGenerationPost";

const PinterestPost: React.FC<PinterestPostProps> = ({ post: sourcePost, setLoading, hideDatetime = false, contentCharLimit }) => {
    const post = withoutLeadGenerationBody(sourcePost);
    const isMobile = useIsMobile(900);


    // Get the logged-in user from Redux.
    const user = useSelector((state: RootState) => state.auth.user);

    // State for toggling the expanded text view.
    const [isExpanded, setIsExpanded] = useState(false);

    // Determine character limit based on screen size.
    const CHARACTER_LIMIT = 43;

    // Toggle function for "Read More" / "Show Less"
    const toggleExpand = () => {
        setIsExpanded((prev) => !prev);
    };

    // Combine title, content, and footer for rendering.
    // Two newlines ("\n\n") are added between sections.
    const getCombinedText = (): string => {
        let text = "";
        if (post?.content) text += post?.content;
        if (post?.includeFooter && post?.footer) text += (text ? "\n\n" : "") + post?.footer;
        text = text.replace(/\n\n/g, "\n");
        text = text.replace(/\n/g, " | ");
        return text.trim();
    };

    // Render full text, with newlines preserved via CSS.
    const renderFullContent = () => {
        const rawContent = Array.isArray(post?.content) ? (post?.content as string[]).join("") : (post?.content as string) || "";
        const withinContent = contentCharLimit ? rawContent.substring(0, contentCharLimit) : rawContent;
        const overflowContent = contentCharLimit && rawContent.length > contentCharLimit ? rawContent.substring(contentCharLimit) : "";
        const formattedWithin = withinContent.replace(/\n\n/g, "\n").replace(/\n/g, " | ");
        const formattedOverflow = overflowContent ? overflowContent.replace(/\n\n/g, "\n").replace(/\n/g, " | ") : "";
        return (
          <>
            {(post?.content || (post?.includeFooter && post?.footer)) && (
              <div
                className="text-sm text-gray-800 mb-2 whitespace-pre-line break-words overflow-hidden"
                style={{
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {formattedWithin}{" "}
                {formattedOverflow && (
                    <span className="bg-red-100 line-through text-red-600">{formattedOverflow}</span>
                )}
                {post?.includeFooter && post?.footer &&
                  post?.footer.replace(/\n\n/g, "\n").replace(/\n/g, " | ")}
              </div>
            )}
          </>
        );
    };

    // Render content based on the combined text.
    // When the text is longer than the limit and not expanded,
    // a truncated version is shown with a "Read More" button.
    const renderContent = () => {
        const fullText = getCombinedText();
        if (fullText.length <= CHARACTER_LIMIT || isExpanded) {
            return (
                <>
                    {renderFullContent()}
                    {fullText.length > CHARACTER_LIMIT && (
                        <button
                            onClick={toggleExpand}
                            className="text-blue-500 hover:underline text-sm"
                        >
                            Show Less
                        </button>
                    )}
                </>
            );
        } else {
            let truncated = fullText.slice(0, CHARACTER_LIMIT);
            // Make sure not to cut words in half.
            const lastSpace = truncated.lastIndexOf(" ");
            if (lastSpace > 0) {
                truncated = truncated.slice(0, lastSpace);
            }
            return (
                <>
                    <div className="text-sm text-gray-800 whitespace-pre-line break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {truncated}...
                    </div>
                    <button
                        onClick={toggleExpand}
                        className="text-blue-500 hover:underline text-sm"
                    >
                        Read More
                    </button>
                </>
            );
        }
    };

    // Render the media portion. This example renders only the first media item.
    // If it is a video, a PlayCircle icon overlay is shown.
    const renderMedia = () => {
        // Check if this is a Lead Generation post (either type "Lead Generation" or preview with styling)
        const isLeadGenerationPost = post?.type === 'Lead Generation' || 
                                    (post?.type === 'preview' && post?.background && post?.textColor);

        // For Lead Generation posts: if image is generated, display it instead of re-rendering styled block
        if (isLeadGenerationPost && post?.media && post?.media.length > 0 && post.media[0].signedUrl) {
            return (
                <img
                    src={post.media[0].signedUrl}
                    alt="Lead Generation Post"
                    className="object-contain w-full h-full"
                />
            );
        }

        // If backgroundColor and textColor are present, render styled content block instead of media
        if (post?.background && post?.textColor) {
            // Use imageText for the graphic if available, otherwise use content
            const graphicText = (post as any).imageText || post.content;
            // Use raw content with newlines preserved for preview block
            const content = Array.isArray(graphicText)
                ? graphicText.join("\n\n")
                : graphicText || "";

            // Helper function to get background style
            const getBackgroundStyle = (bg: any) => {
                if (!bg) return undefined;
                if (bg.type === 'gradient') {
                    return `linear-gradient(${bg.direction || 'to right'}, ${bg.from || '#000000'}, ${bg.to || '#FFFFFF'})`;
                }
                return bg.color || '#000000';
            };

            if (isMobile) {
                // Mobile: fluid layout — fills container width, locked to 2:3 aspect ratio
                const mobileFontSize = calculateFittedFontSize(content, post.fontSize || 16, post.fontFamily, 250, 375);
                return (
                    <div className="w-full flex justify-center">
                        <div
                            className="flex items-center justify-center rounded-lg w-full"
                            style={{
                                aspectRatio: "2/3",
                                background: getBackgroundStyle(post.background),
                                color: post.textColor,
                                fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                                padding: "8%",
                            }}
                        >
                            <p
                                className="font-bold text-center break-words"
                                style={{
                                    fontSize: `${mobileFontSize}px`,
                                    lineHeight: 1.5,
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    width: "100%",
                                }}
                            >
                                {content}
                            </p>
                        </div>
                    </div>
                );
            }

            // Desktop: original pixel-based rendering (unchanged)
            const platformDimensions = { width: 1000, height: 1500 }; // Pinterest tall dimensions
            const maxWidth = 400;
            const scale = maxWidth / platformDimensions.width;
            const displayWidth = platformDimensions.width * scale;
            const displayHeight = platformDimensions.height * scale;
            const baseFontSize = post.fontSize || 16;
            const fittedFontSize = calculateFittedFontSize(content, baseFontSize, post.fontFamily, displayWidth, displayHeight);
            const horizontalPadding = Math.round(displayWidth * 0.08);
            const verticalPadding = Math.round(displayHeight * 0.08);

            return (
                <div className="w-full flex justify-center">
                    <div className="flex items-center justify-center rounded-lg" 
                         style={{ 
                             background: getBackgroundStyle(post.background), 
                             color: post.textColor,
                             fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                             width: `${displayWidth}px`,
                             height: `${displayHeight}px`,
                             paddingLeft: `${horizontalPadding}px`,
                             paddingRight: `${horizontalPadding}px`,
                             paddingTop: `${verticalPadding}px`,
                             paddingBottom: `${verticalPadding}px`,
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                         }}>
                        <p className="font-bold text-center break-words"
                           style={{
                               fontSize: `${fittedFontSize}px`,
                               lineHeight: 1.5,
                               wordBreak: 'break-word',
                               overflowWrap: 'anywhere',
                           }}>
                            {content}
                        </p>
                    </div>
                </div>
            );
        }

        if (post?.media && post?.media.length > 0) {
            const media = post?.media[0];
            if (
                media.type === MediaType.AssetVideo ||
                media.type === MediaType.ListingVideo
            ) {
                return (
                    <div className="relative w-full h-full">
                        <video
                            muted
                            src={media.signedUrl}
                            className="object-cover w-full h-full"
                            playsInline
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <PlayCircle className="w-12 h-12 text-white opacity-75" />
                        </div>
                    </div>
                );
            }
            return (
                <img
                    src={media.signedUrl}
                    alt="Post media"
                    className="object-contain w-full h-full"
                />
            );
        }
        // Fallback placeholder image.
        return (
            <img
                src="/placeholder.svg?height=800&width=600"
                alt="Placeholder"
                className="object-cover w-full h-full"
            />
        );
    };

    return (
      <div
        className={`flex ${
          isMobile ? "flex-col" : "flex-row"
        } max-w-5xl mx-auto rounded-2xl overflow-hidden border shadow-sm h-full`}
      >
        {/* Left side: Media */}
        {/* <div className="relative w-full md:w-3/5" style={{ height: post?.background && post?.textColor ? 'auto' : '500px' }}>
                {renderMedia()}
            </div> */}

        <div
          className="relative w-full md:w-3/5"
          style={{
            height: post?.background && post?.textColor ? "auto" : "500px",
          }}
        >
          {renderMedia()}
        </div>

        {/* Right side: Post Content */}
        <div className="w-full min-h-full p-4 flex flex-col justify-between">
          <div>
            {/* Top Action Bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1">
                  <Heart className="w-5 h-5" />
                  <span className="text-sm">5</span>
                </button>
                <button>
                  <Share2 className="w-5 h-5" />
                </button>
                <button>
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              </div>
              <button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 py-2">
                Save
              </button>
            </div>
            <div className="mb-4">
              {post?.title && (
                <div className="text-xl font-bold mb-2 whitespace-pre-line break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                  {post?.title}
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Avatar className="w-6 h-6">
                  <AvatarImage
                    src={
                      post?.userImage ||
                      user?.profileImage ||
                      "/placeholder-avatar.svg"
                    }
                    alt="User"
                  />
                  <AvatarFallback>
                    {user && user.nameFirst && user.nameLast
                      ? user.nameFirst[0].toUpperCase() +
                        user.nameLast[0].toUpperCase()
                      : "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm">
                  {post?.displayName ? (
                    <>
                      <a
                        href={post?.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {post?.displayName}
                      </a>
                    </>
                  ) : (
                    <>
                      {user?.nameFirst || "User"} {user?.nameLast || ""}
                    </>
                  )}
                </span>
              </div>
            </div>
            {/* Title, Content, and Footer with toggling logic */}
            {/* Don't render content text if using background colors (it's rendered in renderMedia) */}
            {((post as any)?.contentType === "Caption Video" || (post as any)?.type === "Caption Video") &&
            !post?.content &&
            (post?.status?.toLowerCase() === "draft" || post?.status?.toLowerCase() === "videoprocessing") ? (
                <div className="space-y-2 py-1">
                    <div className="h-3 bg-gray-200 rounded w-3/4 shimmer-bar" />
                    <div className="h-3 bg-gray-200 rounded w-1/2 shimmer-bar" />
                </div>
            ) : renderContent()}
          </div>

          {/* Comment Input */}
          <div className="flex items-center gap-2 mt-3 p-3 bg-gray-100 rounded-full">
            <span className="text-gray-400 text-sm flex-1">Add a comment</span>
            <div className="flex justify-center items-center gap-2">
              <GoSmiley className="text-black" />
              <PiSticker className="text-black" />
              <GrGallery className="text-black" />
            </div>
          </div>
        </div>
      </div>
    );
};

export default PinterestPost;
